import React from 'react';
import { Link } from 'react-router-dom';
import { CpuChipIcon, WrenchScrewdriverIcon, CodeBracketIcon, CloudArrowUpIcon, AcademicCapIcon, ChatBubbleLeftRightIcon, ArrowRightIcon, BoltIcon, CircleStackIcon, PresentationChartBarIcon } from '@heroicons/react/24/outline';

const PageHeader = ({ title, subtitle }) => (
  <div className="bg-figma-dark-bg-secondary py-16 md:py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-figma-dark-text-primary animate-fade-in-up">{title}</h1>
      {subtitle && <p className="mt-4 text-lg text-figma-dark-text-secondary max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.1s'}}>{subtitle}</p>}
    </div>
  </div>
);

const ServiceCard = ({ icon: Icon, title, children, linkTo, imageUrl, animationDelay }) => (
  <div className="bg-figma-dark-bg-secondary border border-figma-dark-border rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col group hover:animate-subtle-3d-tilt-hover animate-fade-in-up" style={{ animationDelay }}>
    {imageUrl && (
      <div className="w-full h-52 bg-figma-dark-bg-tertiary overflow-hidden">
        <img src={imageUrl} alt={title || 'Service image'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
      </div>
    )}
    <div className="p-6 flex flex-col flex-grow">
        <div className="flex items-start mb-4">
            {Icon && <Icon className="h-8 w-8 text-figma-dark-primary mr-4 mt-1 flex-shrink-0" />}
            <h3 className="text-xl font-semibold text-figma-dark-text-primary group-hover:text-figma-dark-primary transition-colors">{title}</h3>
        </div>
        <p className="text-base text-figma-dark-text-secondary leading-relaxed mb-6 flex-grow">{children}</p>
        {linkTo && (
            <Link to={linkTo} className="text-sm font-semibold text-figma-dark-primary hover:text-figma-dark-primary-hover inline-flex items-center mt-auto group-hover:underline">
                Learn More <ArrowRightIcon className="ml-1.5 h-4 w-4" />
            </Link>
        )}
    </div>
  </div>
);

const ServicesPage = () => {
  const services = [
    {
      icon: CpuChipIcon,
      title: 'AI & Machine Learning',
      description: 'Unlock the power of your data with custom AI models, predictive analytics, NLP, and computer vision systems designed to automate and inform.',
      link: '/solutions/ai-development',
      imageUrl: 'https://placehold.co/600x400/7F5AF0/16161A?text=AI+Powered+Insights&font=montserrat' // Figma primary
    },
    {
      icon: BoltIcon,
      title: 'Robotics & Process Automation',
      description: 'Implement intelligent automation with advanced robotics and RPA solutions to streamline operations, enhance productivity, and reduce costs.',
      link: '/solutions/robotics',
      imageUrl: 'https://placehold.co/600x400/2CB67D/16161A?text=Smart+Automation&font=montserrat' // Figma accent
    },
    {
      icon: CodeBracketIcon,
      title: 'Custom Software Engineering',
      description: 'End-to-end development of web platforms, mobile applications, and enterprise software, crafted with precision and scalability in mind.',
      link: '/services/software-development',
      imageUrl: 'https://placehold.co/600x400/242630/EFEFF1?text=Bespoke+Software&font=montserrat' // Figma bg-secondary
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Cloud Native Solutions',
      description: 'Leverage the cloud with our expertise in strategy, migration, and management. We build resilient, scalable applications using modern DevOps practices.',
      link: '/services/cloud-solutions',
      imageUrl: 'https://placehold.co/600x400/16161A/94A3B8?text=Scalable+Cloud+Infra&font=montserrat' // Figma bg-primary
    },
    {
      icon: AcademicCapIcon,
      title: 'Technology Advisory',
      description: 'Strategic guidance on technology roadmaps, digital transformation, and AI adoption to ensure your technology investments align with your business objectives.',
      link: '/contact?interest=Consulting',
      imageUrl: 'https://placehold.co/600x400/3E414D/EFEFF1?text=Strategic+Guidance&font=montserrat' // Figma border
    },
    {
      icon: PresentationChartBarIcon, // Changed from CircleStackIcon for variety
      title: 'Data Analytics & BI',
      description: 'Transform raw data into actionable intelligence with our comprehensive data analytics and business intelligence services, driving informed decisions.',
      link: '/contact?interest=DataAnalytics',
      imageUrl: 'https://placehold.co/600x400/2F323D/EFEFF1?text=Data-Driven+Decisions&font=montserrat' // Figma bg-tertiary
    },
  ];

  return (
    <div className="bg-figma-dark-bg-primary min-h-screen">
      <PageHeader 
        title="Our Services"
        subtitle="We provide a comprehensive suite of technology services designed to help you innovate, optimize, and scale. Discover how we can empower your business in the digital age."
      />
      
      <section className="py-12 md:py-16 bg-figma-dark-bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg shadow-2xl overflow-hidden animate-fade-in-up">
            <img 
              src="https://placehold.co/1200x450/242630/7F5AF0?text=Empowering+Digital+Transformation&font=montserrat"
              alt="Montage of various technology services and solutions in a dark theme"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-figma-dark-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16 animate-fade-in-up">
                <h2 className="text-3xl font-bold text-figma-dark-text-primary">Explore Our Service Offerings</h2>
                <p className="mt-4 text-lg text-figma-dark-text-secondary max-w-2xl mx-auto">
                    Tailored technology solutions to meet your specific business needs and drive impactful results across your enterprise.
                </p>
            </div>
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard 
                  key={service.title}
                  icon={service.icon} 
                  title={service.title} 
                  linkTo={service.link}
                  imageUrl={service.imageUrl}
                  animationDelay={`${index * 0.1}s`}
              >
                {service.description}
              </ServiceCard>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Figma Dark Style */}
      <section className="bg-figma-dark-primary py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Elevate Your Technology Stack?
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-xl mx-auto">
            Our team is ready to discuss your unique requirements and how our expertise can bring your vision to life. 
          </p>
          <Link
            to="/contact"
            className="bg-figma-dark-bg-secondary hover:bg-figma-dark-bg-tertiary text-figma-dark-text-primary font-semibold px-10 py-3.5 rounded-lg text-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
          >
            Request a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage; 